import { useEffect, useState } from 'react';
import {
  useQuery,
  useLazyQuery,
  LazyQueryHookOptions,
} from '@apollo/react-hooks';
import { compose, pathOr, propOr, find, propEq, isEmpty } from 'ramda';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

type Category = {
  subCategory: SubCategory[];
};

type Tag = {
  id: string;
  text: string;
};

type SubCategory = {
  id: string | number;
  text: string;
  tags: Tag[];
};

export type FoundCategory = {
  id: string | number;
  text: string;
  description: string;
};

type OptionData = {
  id: string | number;
  text: string;
  tags?: Tag[];
};

type CategoryData = {
  data: Category[];
};

type Option = {
  key: string | number;
  text: string;
  value: string | number | null;
};

type DropDown = {
  options: Option[];
  label: {
    children: string;
  };
  placeholder: string;
};

const GET_CATEGORY_DATA = gql`
  query getSubCategories {
    getSubCategories {
      subCategories {
        id
        text
        tags {
          id
          text
        }
      }
    }
  }
`;

const GET_CATEGORY = gql`
  query getCategoryById($params: CategoryInput) {
    getCategoryById(params: $params) {
      foundCategory {
        id
        text
        description
      }
    }
  }
`;

const categoryTemplate = {
  options: [
    {
      key: '-999',
      text: 'Select One',
      value: '',
    },
  ],
  label: {
    children: '',
  },
  placeholder: 'Category',
};

const buildOptions = (optionsData: OptionData[]) =>
  optionsData.map((optionData: OptionData) => ({
    key: optionData.id,
    text: optionData.text,
    value: optionData.id.toString(),
  }));

const getTags = (categoryId: string, categoryData: SubCategory[]) =>
  compose(propOr([], 'tags'), find(propEq('id', categoryId)))(categoryData);

export const useCategories = (
  currCategory?: string,
  query?: DocumentNode,
  variables?: Object,
) => {
  const [rawData, setRawData] = useState<SubCategory[]>([]);
  const [subCategories, setSubCategories] = useState<Option[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<DropDown>(
    categoryTemplate,
  );
  const [subCategoryOptions, setSubCategoryOptions] = useState<DropDown>(
    categoryTemplate,
  );

  const [category, setCategory] = useState<any>();
  const [tags, setTags] = useState<Option[]>([]);

  const { data: categoryData = [], loading } = useQuery<CategoryData>(
    GET_CATEGORY_DATA,
  );

  useEffect(() => {
    if (loading) return;

    const data = ([] = pathOr(
      [],
      ['getSubCategories', 'subCategories'],
      categoryData,
    ));
    const subCategories = buildOptions(data);

    setRawData(data);
    setSubCategories(subCategories);
    if (currCategory) {
      setCategory(currCategory);
    }
  }, [categoryData]);

  useEffect(() => {
    setCategoryOptions({
      options: subCategories,
      label: {
        children: 'Categories',
      },
      placeholder: 'Category',
    });
  }, [subCategories]);

  useEffect(() => {
    setSubCategoryOptions({
      options: tags,
      label: {
        children: 'Sub Categories',
      },
      placeholder: 'Sub Category',
    });
  }, [tags]);

  useEffect(() => {
    const tags = getSecondaryOptions(category);
    setTags(tags);
  }, [category]);

  const getSecondaryOptions = (id: string) => {
    const data = getTags(id, rawData);
    if (isEmpty(data)) return [];

    return buildOptions(data as OptionData[]);
  };

  const [
    getCategoryById,
    { loading: categoryByIdLoading, data: foundCategory },
  ] = useLazyQuery<FoundCategory>(
    GET_CATEGORY as DocumentNode,
    {
      variables,
      fetchPolicy: 'no-cache',
    } as LazyQueryHookOptions,
  );

  const [
    getSubCategoryById,
    { loading: subCategoryByIdLoading, data: foundSubCategory },
  ] = useLazyQuery<FoundCategory>(
    GET_CATEGORY as DocumentNode,
    {
      variables,
      fetchPolicy: 'no-cache',
    } as LazyQueryHookOptions,
  );

  return {
    setCategory,
    getCategoryById,
    getSubCategoryById,
    categoryOptions,
    subCategoryOptions,
    category,
    categoryByIdLoading,
    foundCategory,
    subCategoryByIdLoading,
    foundSubCategory,
  };
};
